import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const PHRASES = ["Create.", "Direct.", "Render it.", "Deliver."];

const frames = [
  { src: "/assets/frames/frame-1.jpg", alt: "Delivery driver floating in space among flying products", span: "col-span-2", ratio: "aspect-[21/9]" },
  { src: "/assets/frames/frame-2.jpg", alt: "Camel in the desert", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-3.jpg", alt: "Laughing man close-up", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-6.jpg", alt: "Samurai holding a wooden mallet over his shoulder", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-7.jpg", alt: "Woman with hair curlers in a dark kitchen", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-9.jpg", alt: "Man in a puffy jacket caught at an open fridge at night", span: "col-span-2", ratio: "aspect-[21/9]" },
  { src: "/assets/frames/frame-8.jpg", alt: "Woman in a red headscarf, close-up", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-4.jpg", alt: "Astronaut walking on the moon", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-5.jpg", alt: "Monkey drinking a soda near a burning circus tent", span: "", ratio: "aspect-[4/3]" },
  { src: "/assets/frames/frame-10.jpg", alt: "Mustached man in a striped suit on the phone at golden hour", span: "", ratio: "aspect-[4/3]" },
];

export default function Craft() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % PHRASES.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="craft"
      data-testid="craft-section"
      className="border-t border-white/10 px-6 py-28 md:px-10 md:py-36"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="order-2 lg:order-1 lg:col-span-7"
        >
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            {frames.map((frame, i) => (
              <div
                key={frame.src}
                data-testid={`craft-frame-${i + 1}`}
                className={`group relative overflow-hidden bg-[#050505] ${frame.span} ${frame.ratio}`}
              >
                <img
                  src={frame.src}
                  alt={frame.alt}
                  loading="lazy"
                  className="h-full w-full object-cover brightness-90 transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.04] group-hover:brightness-105"
                />
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="order-1 lg:order-2 lg:col-span-5"
        >
          <h2 className="font-mono text-[10px] font-normal tracking-[0.45em] text-zinc-500">
            FROM IDEA TO AIR
          </h2>
          <motion.div
            data-testid="craft-rotating-text"
            aria-live="polite"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } },
            }}
            className="mt-8 flex flex-col gap-2 font-serif text-5xl leading-[1.15] tracking-tight sm:text-6xl lg:gap-3 lg:text-7xl"
          >
            {PHRASES.map((phrase, i) => (
              <span
                key={phrase}
                data-testid={`craft-line-${i + 1}`}
                className={`block ${
                  index === i ? "text-[#E1A730]" : "text-zinc-700"
                }`}
              >
                <span className="inline-block overflow-hidden pb-1 align-bottom">
                  <motion.span
                    className="inline-block transition-colors duration-500"
                    variants={{
                      hidden: { y: "110%" },
                      show: { y: 0, transition: { duration: 0.8, ease: EASE } },
                    }}
                  >
                    We{" "}
                  </motion.span>
                </span>
                <span className="inline-block overflow-hidden pb-1 align-bottom">
                  <motion.span
                    className="inline-block"
                    variants={{
                      hidden: { y: "110%" },
                      show: { y: 0, transition: { duration: 0.8, ease: EASE } },
                    }}
                  >
                    <em
                      className={`transition-colors duration-500 ${
                        index === i ? "text-[#E1A730]" : "text-zinc-800"
                      }`}
                    >
                      {phrase}
                    </em>
                  </motion.span>
                </span>
              </span>
            ))}
          </motion.div>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-zinc-500">
            Every frame above started as an idea in a room. Concept, direction, AI rendering and
            broadcast delivery under one roof.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
